import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ENDPOINT } from 'constants/variables';
import { helpHttp } from 'service/HelpHttp';
import { warningErrorAction } from 'actions/ErrorActions';
import DecaDossierBuilder from './DecaDossierBuilder';

const STATUS_LABELS = {
  ACTIVE: 'Vigente',
  SUPERSEDED: 'Sustituida',
  FINISHED: 'Finalizada',
  DISABLED: 'Deshabilitada',
  DRAFT: 'Borrador'
};

const FIELD_LABELS = {
  'shipping.ownformdi_id': 'Documento DI asociado',
  'formdi.ori_tras_documentid': 'NIF/CIF del origen',
  'formdi.ori_tras_tradename': 'Razón social del origen',
  'formdi.ori_tras_street': 'Dirección del origen',
  'formdi.ori_tras_nima': 'NIMA del origen',
  'formdi.dest_tras_documentid': 'NIF/CIF del destino',
  'formdi.dest_tras_tradename': 'Razón social del destino',
  'formdi.dest_tras_street': 'Dirección del destino',
  'formdi.dest_tras_nima': 'NIMA del destino',
  'formdi.rel_trans_documentid': 'NIF/CIF del transportista',
  'formdi.rel_trans_tradename': 'Transportista',
  'shipping.vehiclestore_licenseplate': 'Matrícula',
  'shipping.vehiclestore_trailer': 'Remolque',
  'shipping.cranemodel_namees': 'Modelo de grúa',
  'shipping.semitrailertype_namees': 'Tipo de semirremolque',
  'shipping.date_shipping': 'Fecha de la carta de portes',
  'shipping.origin_street': 'Lugar de origen',
  'shipping.destine_street': 'Lugar de destino',
  'shipping.observations': 'Observaciones',
  'shipping.linesshipping': 'Artículos, residuos y cantidades'
};

const VISIBLE_CHANGE_FIELDS = new Set(Object.keys(FIELD_LABELS));

const displayValue = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'object') {
    const text = JSON.stringify(value);
    return text.length > 180 ? `${text.slice(0, 180)}…` : text;
  }
  if (typeof value === 'boolean') return value ? 'Sí' : 'No';
  return String(value);
};

const parseChanges = (value) => {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

const articleKey = (article, index) => String(
  article?.id ?? article?.article_id ?? article?.indexrowarticle ?? index
);

const articleSummary = (article) => {
  if (!article) return null;
  const name = article.article_namees || article.article_nameen || 'Artículo';
  const ler = article.article_codeler || 'Sin código LER';
  const amount = article.amount === null || article.amount === undefined
    ? 'Sin cantidad'
    : `${Number(article.amount).toLocaleString('es-ES', { maximumFractionDigits: 2 })} kg`;
  return `${name} · LER ${ler} · ${amount}`;
};

const expandArticleChanges = (change) => {
  const previous = Array.isArray(change.previousValue) ? change.previousValue : [];
  const current = Array.isArray(change.newValue) ? change.newValue : [];
  const previousByKey = new Map(previous.map((article, index) => [articleKey(article, index), article]));
  const currentByKey = new Map(current.map((article, index) => [articleKey(article, index), article]));
  return [...new Set([...previousByKey.keys(), ...currentByKey.keys()])]
    .map((key) => {
      const previousArticle = previousByKey.get(key);
      const currentArticle = currentByKey.get(key);
      const previousSummary = articleSummary(previousArticle);
      const currentSummary = articleSummary(currentArticle);
      if (previousSummary === currentSummary) return null;
      return {
        ...change,
        label: `Artículo: ${currentArticle?.article_namees || previousArticle?.article_namees || key}`,
        previousValue: previousSummary,
        newValue: currentSummary
      };
    })
    .filter(Boolean);
};

const visibleChanges = (value) => parseChanges(value)
  .filter((change) => VISIBLE_CHANGE_FIELDS.has(change.field))
  .flatMap((change) => (
    change.field === 'shipping.linesshipping'
      ? expandArticleChanges(change)
      : [change]
  ));

const ShippingDecaViewer = ({ id, onShippingLoaded, onServiceLoaded }) => {
  const dispatch = useDispatch();
  const token = useSelector((state) => state.loginUser.token);
  const [shipping, setShipping] = useState(null);
  const [service, setService] = useState(null);
  const [history, setHistory] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [error, setError] = useState(null);

  const replacePdfUrl = useCallback((blob) => {
    setPdfUrl((currentUrl) => {
      if (currentUrl) URL.revokeObjectURL(currentUrl);
      return URL.createObjectURL(blob);
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const shippingData = await helpHttp().get2(
          ENDPOINT, 'shipping/getshippingbyid/', token, id
        );
        const serviceData = await helpHttp().get(
          ENDPOINT, `deca/transport-services/shipping/${id}`, token
        );
        if (!shippingData || !serviceData?.id) throw new Error('service');
        const [activeDocument, documentHistory] = await Promise.all([
          helpHttp().get(
            ENDPOINT, `deca/documents/service/${serviceData.id}/active`, token
          ),
          helpHttp().get(
            ENDPOINT, `deca/documents/service/${serviceData.id}/history`, token
          )
        ]);
        const currentDocument = activeDocument?.publicToken ? activeDocument
          : [...(Array.isArray(documentHistory) ? documentHistory : [])].sort((a, b) => b.version - a.version)[0];
        if (!currentDocument?.publicToken) throw new Error('document');
        let archivedBlob = null;
        if (currentDocument.status !== 'ACTIVE' || serviceData.status === 'CANCELLED') {
          const response = await fetch(`${ENDPOINT}/deca/documents/${currentDocument.id}/archive/content`,
            { headers: { Authorization: `Bearer ${token}` } });
          if (!response.ok) throw new Error('archive');
          archivedBlob = await response.blob();
        }
        if (!cancelled) {
          if (onShippingLoaded) onShippingLoaded(shippingData);
          if (onServiceLoaded) onServiceLoaded(serviceData);
          setShipping(shippingData);
          setService(serviceData);
          setHistory(Array.isArray(documentHistory) ? documentHistory : []);
          setSelectedDocument(currentDocument);
          if (archivedBlob) replacePdfUrl(archivedBlob);
        }
      } catch (loadError) {
        if (!cancelled) {
          setError('No se pudo abrir el expediente DeCA');
          dispatch(warningErrorAction('No se pudo abrir el expediente DeCA'));
        }
      }
    };
    load();
    return () => { cancelled = true; };
  }, [dispatch, id, onServiceLoaded, onShippingLoaded, token, replacePdfUrl]);

  useEffect(() => () => {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
  }, [pdfUrl]);

  const selectVersion = useCallback(async (document) => {
    setSelectedDocument(document);
    setPdfUrl((currentUrl) => {
      if (currentUrl) URL.revokeObjectURL(currentUrl);
      return null;
    });
    if (document.status === 'ACTIVE' && service?.status !== 'CANCELLED') return;
    try {
      const response = await fetch(
        `${ENDPOINT}/deca/documents/${document.id}/archive/content`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) throw new Error('history-content');
      replacePdfUrl(await response.blob());
    } catch (downloadError) {
      dispatch(warningErrorAction('No se pudo abrir esta versión del DeCA'));
    }
  }, [dispatch, replacePdfUrl, token, service]);

  const selectedChanges = useMemo(
    () => visibleChanges(selectedDocument?.changeDetails),
    [selectedDocument]
  );
  const activeSelected = selectedDocument?.status === 'ACTIVE' && service?.status !== 'CANCELLED';
  const dossierRow = shipping && selectedDocument ? {
    ...shipping,
    decaPublicToken: selectedDocument.publicToken
  } : null;

  if (error) {
    return <div style={{ padding: '2rem', textAlign: 'center', color: '#e74c3c' }}>{error}</div>;
  }
  if (!shipping || !service || !selectedDocument) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Preparando expediente DeCA…</div>;
  }

  return (
    <div style={{ width: '100%' }}>
      <section className="deca-history-card">
        <div className="deca-history-title">
          <span>{service.status === 'CANCELLED' ? 'DeCA ANULADO · Historial y trazabilidad' : 'Historial y trazabilidad'}</span>
          <span className="deca-history-count">{history.length} versiones</span>
        </div>
        <div className="deca-version-list">
          {history.map((document) => (
            <button
              type="button"
              key={document.id}
              className={`deca-version-button ${selectedDocument.id === document.id ? 'is-selected' : ''}`}
              onClick={() => selectVersion(document)}
            >
              <strong>Versión {document.version}</strong>
              <span className={`deca-status deca-status-${document.status?.toLowerCase()}`}>
                {service.status === 'CANCELLED' ? 'Anulado' : STATUS_LABELS[document.status] || document.status}
              </span>
              <small>{document.createdAt ? new Date(document.createdAt).toLocaleString('es-ES') : '—'}</small>
            </button>
          ))}
        </div>
        <div className="deca-trace-summary">
          <div><strong>Usuario:</strong> {selectedDocument.createdByUsername || 'No disponible'}</div>
          <div><strong>Motivo:</strong> {selectedDocument.modificationReason || (selectedDocument.version === 1 ? 'Creación inicial' : 'No indicado')}</div>
          <div><strong>Huella:</strong> <code>{selectedDocument.sha256 || '—'}</code></div>
        </div>
        {selectedDocument.version > 1 && (
          <div className="deca-change-list">
            <strong>Cambios respecto a la versión anterior</strong>
            {selectedChanges.length === 0 ? (
              <div className="deca-no-changes">No hay diferencias estructuradas disponibles para esta versión.</div>
            ) : selectedChanges.map((change, index) => (
              <div className="deca-change-row" key={`${change.field}-${index}`}>
                <span className="deca-change-field">{change.label || FIELD_LABELS[change.field] || change.field}</span>
                <span className="deca-change-old">{displayValue(change.previousValue)}</span>
                <span className="deca-change-arrow">→</span>
                <span className="deca-change-new">{displayValue(change.newValue)}</span>
              </div>
            ))}
          </div>
        )}
      </section>

      <div className="deca-pdf-container">
        {activeSelected && !pdfUrl && dossierRow && (
          <DecaDossierBuilder row={dossierRow} onReady={replacePdfUrl} />
        )}
        {!pdfUrl && <div style={{ padding: '2rem', textAlign: 'center' }}>Preparando versión {selectedDocument.version}…</div>}
        {pdfUrl && (
          <iframe
            title={`Expediente DeCA versión ${selectedDocument.version}`}
            src={pdfUrl}
            style={{ width: '100%', height: '1180px', border: 0 }}
          />
        )}
      </div>
    </div>
  );
};

export default ShippingDecaViewer;
